import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import FormField from "../../../components/ui/FormField";
import { Button } from "../../../components/Button";
import { Mbp3RegisterApi } from "../../../api/Mbp3RegisterApi";
import { ArrowLeft, Trash2 } from "lucide-react";

const mapEntryToDTO = (entry) => ({
  boardResolutionDate: entry.resolutionDate || null,
  investmentDate: entry.investDate || null,
  holderName: entry.holderName || null,
  holderEmail: entry.holderContact || null,
  purposeOfInvestment: entry.purpose || null,
  investeeCompanyName: entry.companyInvested || null,
  classOfSecurities: entry.securityClass || null,
  numberOfSecurities: entry.securityCount ? parseInt(entry.securityCount, 10) : null,
  clientIdOrDpNo: entry.dpId || null,
  faceValueOfSecurities: entry.faceValue ? parseFloat(entry.faceValue) : null,
  paidUpValueOfSecurities: entry.paidValue ? parseFloat(entry.paidValue) : null,
  costOfAcquisition: entry.costAcquisition ? parseFloat(entry.costAcquisition) : null,
  dateOfDisposal: entry.disposalDate || null,
  securitiesDisposed: entry.disposedCount ? parseInt(entry.disposedCount, 10) : 0,
  saleConsideration: entry.saleAmount ? parseFloat(entry.saleAmount) : 0.0,
  balanceSecurities: entry.balanceSec ? parseInt(entry.balanceSec, 10) : null,
  remarks: entry.remarks || null,
});

const createDefaultEntry = () => ({ id: Date.now() + Math.random() });

const MbpThreeForm = () => {
  const token = useSelector((state) => state.auth.access_token);
  const companies = useSelector((state) => state.account.accountDetails);

  const [accountId, setAccountId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const [signatureInfo, setSignatureInfo] = useState(() => {
    const saved = localStorage.getItem("mbp3_signature_info");
    return saved ? JSON.parse(saved) : { signature: "", date: "" };
  });

  const [entries, setEntries] = useState(() => {
    const saved = localStorage.getItem("mbp3_register_data");
    return saved ? JSON.parse(saved) : [createDefaultEntry()];
  });

  useEffect(() => {
    localStorage.setItem("mbp3_register_data", JSON.stringify(entries));
  }, [entries]);

  useEffect(() => {
    localStorage.setItem("mbp3_signature_info", JSON.stringify(signatureInfo));
  }, [signatureInfo]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCompanyChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === "") {
      setAccountId("");
      return;
    }
    const selectedCompany = companies.find(
      (company) => String(company.id) === String(selectedValue)
    );
    if (selectedCompany) {
      setAccountId(selectedCompany.id);
    } else {
      console.error("Company not found for value:", selectedValue);
    }
  };

  const updateSignature = (field, value) => {
    setSignatureInfo((prev) => ({ ...prev, [field]: value }));
  };

  const updateField = (id, field, value) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };

  const addEntry = () => {
    setEntries((prev) => [...prev, createDefaultEntry()]);
  };

  const deleteEntry = (id) => {
    if (entries.length > 1) {
      setEntries((prev) => prev.filter((e) => e.id !== id));
    }
  };

  // ── Save / Submit ──────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!accountId) {
      setSubmitStatus({
        type: "error",
        message: "Please select a company before saving.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const payload = {
        signature: signatureInfo.signature || null,
        mbp3RegisterEntries: entries.map(mapEntryToDTO),
      };

      await Mbp3RegisterApi.submitMbp3Form(payload, accountId, token);

      setSubmitStatus({
        type: "success",
        message: "MBP-3 Register data saved successfully!",
      });

      localStorage.removeItem("mbp3_register_data");
      localStorage.removeItem("mbp3_signature_info");

      setEntries([createDefaultEntry()]);
      setSignatureInfo({ signature: "", date: "" });
      setAccountId("");
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message:
          error.message ||
          "Failed to save MBP-3 register data. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Form Header */}
      <div className="bg-white px-6 py-2 mb-2 border-b-4 rounded-xl relative shadow text-center">
        <button
          onClick={() => window.history.back()}
          className="absolute top-3 left-4 flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>
        <h1 className="text-2xl text-gray-900 uppercase">Form MBP - 3</h1>
        <h2 className="text-lg font-semibold mt-1">
          Register of investments not held in its own name by the company
        </h2>
        <p className="text-xs mt-1 text-gray-500 italic">
          [Pursuant to section 187(3) and rule 14(1)]
        </p>
      </div>

      {/* Company Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name of the company *
        </label>
        <select
          value={accountId}
          onChange={handleCompanyChange}
          className="w-50 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select a company</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.accountName || `Company ${company.id}`}
            </option>
          ))}
        </select>
      </div>

      {/* Status Message */}
      {submitStatus && (
        <div
          className={`mb-3 px-4 py-3 rounded-lg text-sm font-medium border ${
            submitStatus.type === "success"
              ? "bg-green-50 text-green-800 border-green-200"
              : "bg-red-50 text-red-800 border-red-200"
          }`}
        >
          {submitStatus.message}
        </div>
      )}

      {/* Entries */}
      <div className="space-y-2">
        {entries.map((entry, idx) => (
          <div key={entry.id} className="bg-white rounded-xl shadow border overflow-hidden">
            <div className="bg-primary text-white px-3 py-2 flex justify-between items-center">
              <span className="text-sm font-semibold text-white uppercase">Entry #{idx + 1}</span>
              {entries.length > 1 && (
                <button
                  onClick={() => deleteEntry(entry.id)}
                  className="text-red-600 bg-white p-1 rounded-md"
                >
                  <Trash2 size={18}/>
                </button>
              )}
            </div>

            <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-2">
              <FormField
                type="date"
                label="(1) Board resolution date"
                value={entry.resolutionDate}
                onChange={(v) => updateField(entry.id, "resolutionDate", v)}
              />
              <FormField
                type="date"
                label="(2) Date of investment"
                value={entry.investDate}
                onChange={(v) => updateField(entry.id, "investDate", v)}
              />
              <FormField
                label="(3) Holder name"
                value={entry.holderName}
                onChange={(v) => updateField(entry.id, "holderName", v)}
              />
              <FormField
                type="description"
                label="(4) Holder Email"
                value={entry.holderContact}
                onChange={(v) => updateField(entry.id, "holderContact", v)}
              />
              <FormField
                type="description"
                label="(5) Purpose of investment"
                value={entry.purpose}
                onChange={(v) => updateField(entry.id, "purpose", v)}
              />
              <FormField
                label="(6) Investee company name"
                value={entry.companyInvested}
                onChange={(v) => updateField(entry.id, "companyInvested", v)}
              />
              <FormField
                label="(7) Class of securities"
                value={entry.securityClass}
                onChange={(v) => updateField(entry.id, "securityClass", v)}
              />
              <FormField
              type="number"
                label="(8) Number of securities"
                value={entry.securityCount}
                onChange={(v) => updateField(entry.id, "securityCount", v)}
              />
              <FormField
                label="(9) Client ID / DP No."
                value={entry.dpId}
                onChange={(v) => updateField(entry.id, "dpId", v)}
              />
              <FormField
                type="number"
                label="(10) Face value"
                value={entry.faceValue}
                onChange={(v) => updateField(entry.id, "faceValue", v)}
              />
              <FormField
              type="number"
                label="(11) Paid-up value"
                value={entry.paidValue}
                onChange={(v) => updateField(entry.id, "paidValue", v)}
              />
              <FormField
              type="number"
                label="(12) Cost of acquisition"
                value={entry.costAcquisition}
                onChange={(v) => updateField(entry.id, "costAcquisition", v)}
              />
              <FormField
                type="date"
                label="(13) Date of disposal"
                value={entry.disposalDate}
                onChange={(v) => updateField(entry.id, "disposalDate", v)}
              />
              <FormField
              type="number"
                label="(14) Securities disposed"
                value={entry.disposedCount}
                onChange={(v) => updateField(entry.id, "disposedCount", v)}
              />
              <FormField
              type="number"
                label="(15) Sale consideration"
                value={entry.saleAmount}
                onChange={(v) => updateField(entry.id, "saleAmount", v)}
              />
              <FormField
              type="number"
                label="(16) Balance securities"
                value={entry.balanceSec}
                onChange={(v) => updateField(entry.id, "balanceSec", v)}
              />
              <FormField
                type="description"
                label="(17) Remarks"
                value={entry.remarks}
                onChange={(v) => updateField(entry.id, "remarks", v)}
              />
            </div>
          </div>
        ))}
      </div>
<div className="flex flex-row justify-end mt-2">
      <Button label="Add New Entry" onClick={addEntry} className="text-sm"/>
</div>

      {/* Footer — Signature / Date */}
      <div className="mt-2 bg-white p-3 rounded-lg shadow grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          label="Signature (MD/Director/Secretary)"
          value={signatureInfo.signature}
          onChange={(v) => updateSignature("signature", v)}
        />
        <FormField
          type="date"
          label="Date"
          value={signatureInfo.date}
          onChange={(v) => updateSignature("date", v)}
        />
      </div>

      {/* Save Button */}
      <div className="flex w-full justify-end items-center">
        <Button
          label={isSubmitting ? "Saving..." : "Save MBP-3 Register"}
          onClick={handleSave}
          disabled={isSubmitting}
          className="mt-2 mb-3 mr-3 text-xs bg-green-600 hover:bg-green-700 text-white"
        />
      </div>
    </div>
  );
};

export default MbpThreeForm;